// Azir Shifting Sands — soldier spawns behind farthest enemy → Azir dashes forward → impact → dashes back.
// Phases: SOLDIER_SPAWN → DASH_OUT → IMPACT → DASH_BACK → SETTLE

import type { Animation } from "../../engine/animation/AnimationQueue";
import { Easing } from "../../engine/animation/Easing";
import type { Point } from "../../engine/hex/HexLayout";
import type { UnitVisual } from "../UnitVisual";

const SPAWN_MS    = 260;
const DASH_OUT_MS = 340;
const IMPACT_MS   = 180;
const DASH_BACK_MS = 300;
const SETTLE_MS   = 140;
const TOTAL = SPAWN_MS + DASH_OUT_MS + IMPACT_MS + DASH_BACK_MS + SETTLE_MS;

const IMPACT_START  = SPAWN_MS + DASH_OUT_MS;
const RETURN_START  = IMPACT_START + IMPACT_MS;
const SETTLE_START  = RETURN_START + DASH_BACK_MS;

// Pre-baked sand particle angles for the soldier spawn burst
const SAND_COUNT = 20;
const SAND_PARTICLES = Array.from({ length: SAND_COUNT }, (_, i) => ({
  angle: (i / SAND_COUNT) * Math.PI * 2 + (i % 3) * 0.18,
  radius: 0.55 + (i % 4) * 0.18,
  size: 2 + (i % 3),
}));

// Dust streaks trailing behind a dash
const TRAIL_STREAKS = [-0.55, -0.3, -0.12, 0, 0.12, 0.3, 0.55];

export class AzirDashVFX implements Animation {
  private elapsed = 0;
  private impactFired = false;
  private dashAngle: number;
  onEnd?: () => void;

  constructor(
    private visual: UnitVisual,
    private startPx: Point,
    private soldierPx: Point,   // position behind farthest enemy
    private hexSize: number,
    private onImpact: () => void,
  ) {
    this.dashAngle = Math.atan2(soldierPx.y - startPx.y, soldierPx.x - startPx.x);
  }

  update(dt: number): void {
    this.elapsed = Math.min(this.elapsed + dt, TOTAL);

    const dashOut = this.elapsed - SPAWN_MS;
    if (dashOut > 0 && dashOut < DASH_OUT_MS) {
      const t = Easing.easeInCubic(dashOut / DASH_OUT_MS);
      this.visual.x = this.startPx.x + (this.soldierPx.x - this.startPx.x) * t;
      this.visual.y = this.startPx.y + (this.soldierPx.y - this.startPx.y) * t;
    } else if (dashOut >= DASH_OUT_MS && dashOut < DASH_OUT_MS + IMPACT_MS) {
      this.visual.x = this.soldierPx.x;
      this.visual.y = this.soldierPx.y;
    } else if (this.elapsed >= RETURN_START) {
      const rb = this.elapsed - RETURN_START;
      if (rb < DASH_BACK_MS) {
        const t = Easing.easeOutCubic(rb / DASH_BACK_MS);
        this.visual.x = this.soldierPx.x + (this.startPx.x - this.soldierPx.x) * t;
        this.visual.y = this.soldierPx.y + (this.startPx.y - this.soldierPx.y) * t;
      } else {
        this.visual.x = this.startPx.x;
        this.visual.y = this.startPx.y;
      }
    }

    if (!this.impactFired && this.elapsed >= IMPACT_START) {
      this.impactFired = true;
      this.onImpact();
    }

    if (this.elapsed >= TOTAL) this.onEnd?.();
  }

  draw(ctx: CanvasRenderingContext2D): void {
    const e = this.elapsed;

    if (e < SPAWN_MS) {
      this.drawSoldierSpawn(ctx, e / SPAWN_MS);
    } else if (e < IMPACT_START) {
      this.drawSoldierGlow(ctx);
      this.drawDash(ctx, (e - SPAWN_MS) / DASH_OUT_MS, this.startPx, this.soldierPx, 1.0);
    } else if (e < RETURN_START) {
      this.drawImpact(ctx, (e - IMPACT_START) / IMPACT_MS);
    } else if (e < SETTLE_START) {
      const t = (e - RETURN_START) / DASH_BACK_MS;
      this.drawDash(ctx, t, this.soldierPx, this.startPx, 1 - t * 0.5);
    } else {
      this.drawSettle(ctx, (e - SETTLE_START) / SETTLE_MS);
    }
  }

  // Sand soldier materializing — expanding hex ring with sand particles radiating outward
  private drawSoldierSpawn(ctx: CanvasRenderingContext2D, t: number): void {
    const { x, y } = this.soldierPx;
    const ease = Easing.easeOutCubic(t);
    const r = this.hexSize * 0.3 + ease * this.hexSize * 0.85;

    ctx.save();

    // Outer rotating hex outline
    ctx.globalAlpha = (1 - t * 0.3) * 0.85;
    ctx.strokeStyle = "#ffcc44";
    ctx.lineWidth = 2.5;
    ctx.shadowColor = "#ff9900";
    ctx.shadowBlur = 20;
    this.drawHex(ctx, x, y, r * 1.35, t * 2.1);
    ctx.stroke();

    // Inner counter-rotating hex
    ctx.globalAlpha = ease * 0.7;
    ctx.strokeStyle = "#fff0aa";
    ctx.lineWidth = 1.5;
    ctx.shadowBlur = 10;
    this.drawHex(ctx, x, y, r, -t * 3.0);
    ctx.stroke();

    // Radial core glow
    const g = ctx.createRadialGradient(x, y, 0, x, y, r);
    g.addColorStop(0, "rgba(255,240,160,0.95)");
    g.addColorStop(0.35, "rgba(255,180,40,0.6)");
    g.addColorStop(1, "rgba(220,120,0,0)");
    ctx.globalAlpha = ease * 0.85;
    ctx.fillStyle = g;
    ctx.shadowColor = "#ffcc44";
    ctx.shadowBlur = 18;
    ctx.beginPath();
    ctx.arc(x, y, r, 0, Math.PI * 2);
    ctx.fill();

    // Sand particles burst
    ctx.shadowColor = "#ff9900";
    ctx.shadowBlur = 8;
    for (const p of SAND_PARTICLES) {
      const dist = ease * this.hexSize * 1.4 * p.radius;
      const px = x + Math.cos(p.angle) * dist;
      const py = y + Math.sin(p.angle) * dist;
      const fade = 1 - t * 0.8;
      ctx.globalAlpha = fade * 0.8;
      ctx.fillStyle = p.size > 3 ? "#ffcc44" : "#ddaa22";
      ctx.beginPath();
      ctx.arc(px, py, p.size * (1 - t * 0.6), 0, Math.PI * 2);
      ctx.fill();
    }

    ctx.restore();
  }

  // Persistent soldier glow while Azir is dashing toward it
  private drawSoldierGlow(ctx: CanvasRenderingContext2D): void {
    const { x, y } = this.soldierPx;
    const r = this.hexSize * 0.9;
    ctx.save();
    const g = ctx.createRadialGradient(x, y, 0, x, y, r);
    g.addColorStop(0, "rgba(255,220,80,0.4)");
    g.addColorStop(1, "rgba(220,120,0,0)");
    ctx.globalAlpha = 0.6;
    ctx.fillStyle = g;
    ctx.shadowColor = "#ffaa00";
    ctx.shadowBlur = 22;
    ctx.beginPath();
    ctx.arc(x, y, r, 0, Math.PI * 2);
    ctx.fill();
    ctx.restore();
  }

  // Dash trail from fromPx toward toPx at progress t
  private drawDash(ctx: CanvasRenderingContext2D, t: number, fromPx: Point, toPx: Point, intensity: number): void {
    const curX = this.visual.x;
    const curY = this.visual.y;
    const angle = Math.atan2(toPx.y - fromPx.y, toPx.x - fromPx.x);
    const cos = Math.cos(angle);
    const sin = Math.sin(angle);
    const trailLen = Math.hypot(curX - fromPx.x, curY - fromPx.y);

    ctx.save();

    if (trailLen > 4) {
      ctx.translate(fromPx.x, fromPx.y);
      ctx.rotate(angle);

      const fade = intensity * (0.9 - t * 0.3);

      // Outer sand cloud ribbon
      ctx.globalAlpha = fade * 0.38;
      ctx.fillStyle = "#cc8800";
      ctx.shadowColor = "#ffaa22";
      ctx.shadowBlur = 20;
      ctx.fillRect(0, -this.hexSize * 0.58, trailLen, this.hexSize * 1.16);

      // Mid orange streak
      ctx.globalAlpha = fade * 0.7;
      ctx.fillStyle = "#ffaa22";
      ctx.shadowBlur = 10;
      ctx.fillRect(0, -this.hexSize * 0.18, trailLen, this.hexSize * 0.36);

      // Bright gold center
      ctx.globalAlpha = fade * 0.95;
      ctx.fillStyle = "#ffee88";
      ctx.shadowColor = "#ffe060";
      ctx.shadowBlur = 6;
      ctx.fillRect(0, -2.5, trailLen, 5);

      ctx.setTransform(1, 0, 0, 1, 0, 0);
    }

    // Azir glow orb at current position
    const orbR = this.hexSize * 0.5;
    const orbGrad = ctx.createRadialGradient(curX, curY, 0, curX, curY, orbR);
    orbGrad.addColorStop(0, "#fffacc");
    orbGrad.addColorStop(0.3, "#ffcc44");
    orbGrad.addColorStop(0.7, "#ff8800");
    orbGrad.addColorStop(1, "rgba(180,80,0,0)");
    ctx.globalAlpha = intensity * 0.92;
    ctx.fillStyle = orbGrad;
    ctx.shadowColor = "#ffcc44";
    ctx.shadowBlur = 28;
    ctx.beginPath();
    ctx.arc(curX, curY, orbR, 0, Math.PI * 2);
    ctx.fill();

    // Rotating hex on orb
    ctx.globalAlpha = intensity * 0.5;
    ctx.strokeStyle = "#ffee88";
    ctx.lineWidth = 1.5;
    ctx.shadowBlur = 8;
    this.drawHex(ctx, curX, curY, this.hexSize * 0.35, t * 4.5);
    ctx.stroke();

    // Speed lines behind the dash
    ctx.globalAlpha = (1 - t) * intensity * 0.55;
    ctx.strokeStyle = "#ffaa22";
    ctx.lineWidth = 1.5;
    ctx.shadowColor = "#ff8800";
    ctx.shadowBlur = 10;
    for (const offset of TRAIL_STREAKS) {
      const lineLen = (16 + Math.abs(offset) * 14) * (1 - t * 0.5);
      const ox = curX - cos * 8 - sin * offset * this.hexSize * 0.6;
      const oy = curY - sin * 8 + cos * offset * this.hexSize * 0.6;
      ctx.beginPath();
      ctx.moveTo(ox, oy);
      ctx.lineTo(ox - cos * lineLen, oy - sin * lineLen);
      ctx.stroke();
    }

    ctx.restore();
  }

  // Impact burst at soldier position
  private drawImpact(ctx: CanvasRenderingContext2D, t: number): void {
    const { x, y } = this.soldierPx;
    const fade = 1 - Easing.easeInQuad(t);

    ctx.save();

    // White flash
    if (t < 0.25) {
      const flash = 1 - t / 0.25;
      ctx.globalAlpha = flash * 0.85;
      ctx.fillStyle = "#ffffff";
      ctx.shadowColor = "#ffee88";
      ctx.shadowBlur = 30;
      ctx.beginPath();
      ctx.arc(x, y, this.hexSize * 0.95 * flash, 0, Math.PI * 2);
      ctx.fill();
    }

    // Shockwave ring
    const ring1 = this.hexSize * 0.35 + t * this.hexSize * 2.8;
    ctx.globalAlpha = fade * 0.9;
    ctx.strokeStyle = "#ffcc44";
    ctx.lineWidth = 4 - t * 2.5;
    ctx.shadowColor = "#ff9900";
    ctx.shadowBlur = 24;
    ctx.beginPath();
    ctx.arc(x, y, ring1, 0, Math.PI * 2);
    ctx.stroke();

    // Secondary sand ring
    const ring2 = this.hexSize * 0.2 + t * this.hexSize * 1.7;
    ctx.globalAlpha = fade * 0.55;
    ctx.strokeStyle = "#ffaa22";
    ctx.lineWidth = 2;
    ctx.shadowBlur = 14;
    ctx.beginPath();
    ctx.arc(x, y, ring2, 0, Math.PI * 2);
    ctx.stroke();

    // Expanding hex burst
    ctx.globalAlpha = fade * 0.65;
    ctx.strokeStyle = "#ffee88";
    ctx.lineWidth = 1.5;
    ctx.shadowBlur = 10;
    this.drawHex(ctx, x, y, this.hexSize * 0.28 + t * this.hexSize * 2.1, t * 1.6);
    ctx.stroke();

    // Sand particle rays
    ctx.globalAlpha = fade * 0.8;
    ctx.strokeStyle = "#ffcc44";
    ctx.lineWidth = 2;
    ctx.shadowColor = "#ff9900";
    ctx.shadowBlur = 10;
    for (const p of SAND_PARTICLES) {
      const inner = this.hexSize * 0.18 + t * this.hexSize * 0.35;
      const outer = inner + 18 * Easing.easeOutCubic(t) * p.radius;
      ctx.beginPath();
      ctx.moveTo(x + Math.cos(p.angle) * inner, y + Math.sin(p.angle) * inner);
      ctx.lineTo(x + Math.cos(p.angle) * outer, y + Math.sin(p.angle) * outer);
      ctx.stroke();
    }

    ctx.restore();
  }

  // Lingering sand dust after the return dash
  private drawSettle(ctx: CanvasRenderingContext2D, t: number): void {
    const fade = 1 - t;
    const { x, y } = this.startPx;
    ctx.save();
    const g = ctx.createRadialGradient(x, y, 0, x, y, this.hexSize * 0.7);
    g.addColorStop(0, `rgba(255,200,60,${fade * 0.35})`);
    g.addColorStop(1, "rgba(180,80,0,0)");
    ctx.fillStyle = g;
    ctx.shadowColor = "#ffaa22";
    ctx.shadowBlur = 12 * fade;
    ctx.globalAlpha = 1;
    ctx.beginPath();
    ctx.arc(x, y, this.hexSize * 0.7, 0, Math.PI * 2);
    ctx.fill();
    ctx.restore();
  }

  private drawHex(ctx: CanvasRenderingContext2D, cx: number, cy: number, r: number, rot: number): void {
    ctx.beginPath();
    for (let i = 0; i < 6; i++) {
      const a = rot + (i / 6) * Math.PI * 2;
      if (i === 0) ctx.moveTo(cx + Math.cos(a) * r, cy + Math.sin(a) * r);
      else ctx.lineTo(cx + Math.cos(a) * r, cy + Math.sin(a) * r);
    }
    ctx.closePath();
  }

  isDone(): boolean {
    return this.elapsed >= TOTAL;
  }
}
